import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import FeeStatusPill from '../FeeStatusPill';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../ui/accordion';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { toast } from 'sonner';
import { TOAST } from '../../utils/toastMessages';
import { Plus, X, DollarSign, ExternalLink, Copy, Check, AlertCircle, CheckCircle2, EyeOff, Eye, Gift, User, Briefcase, Image as ImageIcon, Settings, Share2, Clock, Star, ChevronRight, Package, Locate, Crown } from 'lucide-react';
import { openShareUrl } from '../../utils/navigation';
import { getVendorAvatar, isUsingOwnerPhotoFallback, hasOwnerPhoto } from '../../utils/vendorImage';
import { VendorAvatar } from '../VendorAvatar';
import { useAppLocation, STATE_ABBR } from '../../contexts/LocationContext';
import { useOnboarding } from '../../context/OnboardingContext';
import {
  VENDOR_TYPE_KEYS,
  VENDOR_TYPE_META,
  VENDOR_TYPE_TO_ALLOWED_CATEGORIES,
  inferVendorTypeFromCategories,
  isCategoryAllowedForType,
} from '../../constants/vendorTypes';

/* ─── Offering Card (shared by packages & services) ─── */
const OfferingCard = ({ svc, idx, services, profileData, setProfileData }) => (
  <div className={`rounded-xl border p-4 ${svc.type === 'package' ? 'border-emerald-200 bg-emerald-50/30' : 'border-gray-200 bg-white'}`} data-testid={`offering-row-${idx}`}>
    <div className="flex items-start justify-between gap-2 mb-3">
      {svc.is_most_popular && (
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold">Most Popular</span>
      )}
      <button
        type="button"
        onClick={() => {
          const updated = [...services];
          updated.splice(idx, 1);
          setProfileData({ ...profileData, services: updated });
        }}
        className="ml-auto text-gray-400 hover:text-red-500"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
    <Input
      value={svc.name || ''}
      placeholder={svc.type === 'package' ? 'Package name' : 'Service name'}
      className="mb-2 font-medium"
      onChange={(e) => {
        const updated = [...services];
        updated[idx] = { ...updated[idx], name: e.target.value };
        setProfileData({ ...profileData, services: updated });
      }}
    />
    <div className="grid grid-cols-2 gap-2 mb-2">
      <Input
        value={svc.starting_price || ''}
        placeholder="Price (e.g., $500)"
        onChange={(e) => {
          const updated = [...services];
          updated[idx] = { ...updated[idx], starting_price: e.target.value };
          setProfileData({ ...profileData, services: updated });
        }}
      />
      <Input
        value={svc.duration || ''}
        placeholder="Duration (e.g., 4-6 hours)"
        onChange={(e) => {
          const updated = [...services];
          updated[idx] = { ...updated[idx], duration: e.target.value };
          setProfileData({ ...profileData, services: updated });
        }}
      />
    </div>
    <Textarea
      value={svc.description || ''}
      placeholder="Brief description"
      rows={2}
      className="text-sm mb-2"
      onChange={(e) => {
        const updated = [...services];
        updated[idx] = { ...updated[idx], description: e.target.value };
        setProfileData({ ...profileData, services: updated });
      }}
    />
    {svc.type === 'package' && (
      <div className="mt-2">
        <p className="text-[11px] font-medium text-gray-500 mb-1">What's included:</p>
        <div className="space-y-1.5">
          {(svc.includes || []).map((item, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0" />
              <Input
                value={item}
                placeholder="Included item"
                className="h-7 text-xs"
                onChange={(e) => {
                  const updated = [...services];
                  const newIncludes = [...(updated[idx].includes || [])];
                  newIncludes[i] = e.target.value;
                  updated[idx] = { ...updated[idx], includes: newIncludes };
                  setProfileData({ ...profileData, services: updated });
                }}
              />
              <button
                type="button"
                onClick={() => {
                  const updated = [...services];
                  const newIncludes = [...(updated[idx].includes || [])];
                  newIncludes.splice(i, 1);
                  updated[idx] = { ...updated[idx], includes: newIncludes };
                  setProfileData({ ...profileData, services: updated });
                }}
                className="text-gray-400 hover:text-red-400"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => {
              const updated = [...services];
              updated[idx] = { ...updated[idx], includes: [...(updated[idx].includes || []), ''] };
              setProfileData({ ...profileData, services: updated });
            }}
            className="text-xs text-emerald-600 font-medium hover:text-emerald-700"
          >
            + Add item
          </button>
        </div>
      </div>
    )}
    <label className="flex items-center gap-2 mt-2 cursor-pointer">
      <input
        type="checkbox"
        checked={svc.is_most_popular || false}
        onChange={(e) => {
          const updated = [...services];
          if (e.target.checked) updated.forEach((s, i) => { if (i !== idx) s.is_most_popular = false; });
          updated[idx] = { ...updated[idx], is_most_popular: e.target.checked };
          setProfileData({ ...profileData, services: updated });
        }}
        className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
      />
      <span className="text-xs text-gray-600">Mark as Most Popular</span>
    </label>
  </div>
);

/* ─── Services Editor Modal ─── */
const ServicesEditorModal = ({ open, onClose, profileData, setProfileData }) => {
  if (!open) return null;
  const services = profileData.services || [];
  const packages = services.map((s, i) => ({ ...s, _idx: i })).filter(s => s.type === 'package');
  const individualServices = services.map((s, i) => ({ ...s, _idx: i })).filter(s => s.type !== 'package');

  const addPackage = () => {
    setProfileData({
      ...profileData,
      services: [...services, { type: 'package', name: '', starting_price: '', description: '', duration: '', includes: [''], is_most_popular: false, available: true }]
    });
  };
  const addService = () => {
    setProfileData({
      ...profileData,
      services: [...services, { type: 'service', name: '', starting_price: '', description: '', duration: '', is_primary: false, available: true }]
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white" data-testid="services-editor-modal">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Services & Offerings</h2>
          <p className="text-xs text-gray-500">Create packages and services customers can request quotes for</p>
        </div>
        <Button variant="outline" size="sm" onClick={onClose} data-testid="services-editor-close">
          Done
        </Button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 py-4 pb-24 space-y-6">
        {/* Helper tip */}
        {services.length === 0 && (
          <div className="text-center py-8 text-gray-400">
            <Package className="w-10 h-10 mx-auto mb-2 opacity-50" />
            <p className="text-sm font-medium text-gray-600">No offerings yet</p>
            <p className="text-xs mt-1 text-gray-400">Most providers create 2-4 packages and a few add-ons</p>
          </div>
        )}

        {/* ── PACKAGES SECTION ── */}
        <div data-testid="packages-section">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h3 className="text-[13px] font-bold text-gray-900">Packages</h3>
              <p className="text-[11px] text-gray-400">Bundled offerings for events</p>
            </div>
            <span className="text-[11px] text-gray-400 font-medium">{packages.length} created</span>
          </div>

          <div className="space-y-3">
            {packages.map((svc) => (
              <OfferingCard key={svc._idx} svc={svc} idx={svc._idx} services={services} profileData={profileData} setProfileData={setProfileData} />
            ))}
          </div>

          <Button
            type="button"
            variant="outline"
            className="w-full mt-3 h-10 rounded-xl border-dashed border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-400 font-semibold text-[13px]"
            data-testid="add-package-btn"
            onClick={addPackage}
          >
            <Plus className="h-4 w-4 mr-1.5" /> Create Package
          </Button>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-100" />

        {/* ── SERVICES SECTION ── */}
        <div data-testid="individual-services-section">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h3 className="text-[13px] font-bold text-gray-900">Services</h3>
              <p className="text-[11px] text-gray-400">Individual items or add-ons</p>
            </div>
            <span className="text-[11px] text-gray-400 font-medium">{individualServices.length} created</span>
          </div>

          <div className="space-y-3">
            {individualServices.map((svc) => (
              <OfferingCard key={svc._idx} svc={svc} idx={svc._idx} services={services} profileData={profileData} setProfileData={setProfileData} />
            ))}
          </div>

          <Button
            type="button"
            variant="outline"
            className="w-full mt-3 h-10 rounded-xl border-dashed border-gray-300 text-gray-600 hover:bg-gray-50 hover:border-gray-400 font-semibold text-[13px]"
            data-testid="add-service-btn"
            onClick={addService}
          >
            <Plus className="h-4 w-4 mr-1.5" /> Add Service
          </Button>
        </div>

        {/* Helper text at bottom */}
        {services.length > 0 && services.length < 3 && (
          <p className="text-[11px] text-gray-400 text-center pt-2">Most providers create 2-4 packages and a few add-ons</p>
        )}
      </div>
    </div>
  );
};

/* ─── Main Form ─── */
const VendorProfileForm = ({
  vendorProfile,
  profileData,
  setProfileData,
  loading,
  user,
  uploadingImage,
  categorySearch,
  setCategorySearch,
  categories,
  states,
  priceRanges,
  getImageUrl,
  handleProfileSubmit,
  handleChange,
  handleProfileImageUpload,
  handlePortfolioImageUpload,
  removeProfilePhoto,
  removePortfolioPhoto,
  handleSetHero,
  addPortfolioField,
  removePortfolioField,
  toggleCategory,
  profileLinkCopied,
  setProfileLinkCopied,
  defaultSection,
  activeSection,
  setActiveSection,
  onSectionViewed,
  handleGoLive,
  publishReadiness,
  isLive,
}) => {
  const navigate = useNavigate();
  const appLoc = useAppLocation();
  const onboarding = useOnboarding();
  // Phase 4: checklist now consumes the canonical backend-computed missing
  // list as its source of truth. Falls back to the legacy publishReadiness
  // prop when the context hasn't loaded yet (e.g., admin impersonation path).
  const canonicalMissing = onboarding?.missing || [];
  const canonicalPhase = onboarding?.phase;
  const useCanonical = canonicalPhase && (canonicalPhase === 'profile_draft' || canonicalPhase === 'ready_to_publish');
  const [referralLinkCopied, setReferralLinkCopied] = React.useState(false);
  const [servicesEditorOpen, setServicesEditorOpen] = React.useState(false);
  const [detectingBizLoc, setDetectingBizLoc] = useState(false);
  const [feeStatus, setFeeStatus] = useState(null);

  // Phase 1: pull vendor's effective platform fee status for read-only display
  React.useEffect(() => {
    const fetchFeeStatus = async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const token = localStorage.getItem('token');
        if (!API_URL || !token) return;
        const res = await axios.get(`${API_URL}/api/vendors/me/fee-status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setFeeStatus(res.data);
      } catch {
        // Silent — informational only
      }
    };
    fetchFeeStatus();
  }, []);

  // Auto-scroll to target section when deep-linked (e.g., "Manage Services" quick action)
  React.useEffect(() => {
    if (defaultSection && defaultSection !== 'basic-info') {
      const timer = setTimeout(() => {
        const el = document.querySelector(`[data-testid="section-${defaultSection}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        if (onSectionViewed) onSectionViewed();
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [defaultSection, onSectionViewed]);

  // "Use my business location" — geo-detect and prefill city/state/zip
  const handleDetectBusinessLocation = async () => {
    setDetectingBizLoc(true);
    const result = await appLoc.detectLocation();
    setDetectingBizLoc(false);
    if (result) {
      // result.city is already passed through normalizeGeocodedCity by
      // LocationContext.detectLocation() — only safe human city names will
      // be present here. If geocoder returned an admin label, result.city
      // will be empty and we leave the field untouched for manual entry.
      // Canonical state representation is 2-letter abbreviation (MS, AL,
      // FL) — `result.state` from LocationContext is already an abbrev.
      setProfileData(prev => ({
        ...prev,
        city: result.city || prev.city,
        state: result.state || prev.state,
        zip_code: result.zip || prev.zip_code
      }));
      toast.success('Location detected');
    } else {
      toast.error('Could not detect location. Please enter manually.');
    }
  };

  const referralLink = user?.referral_code
    ? `https://celeyapp.com/signup?ref=${user.referral_code}`
    : null;

  const shareOnFacebook = async (shareUrl) => {
    const url = encodeURIComponent(shareUrl);
    await openShareUrl(`https://www.facebook.com/sharer/sharer.php?u=${url}`);
  };

  // Counts
  const pkgCount = (profileData.services || []).filter(s => s.type === 'package').length;
  const svcCount = (profileData.services || []).filter(s => s.type !== 'package').length;
  const totalOfferings = pkgCount + svcCount;
  const photoCount = (profileData.portfolio_photos || []).filter(p => p && p.trim()).length;

  return (
    <>
      {/* Phase 4: "Profile Not Yet Visible" amber banner removed.
          The global OnboardingStrip is now the single source of truth for
          profile-incomplete messaging. Admin-paused state is surfaced by a
          separate isolated strip in the app shell. */}

      {/* Profile Header + Preview */}
      {vendorProfile && (
        <div className="mb-4 p-3.5 bg-white border border-gray-200 rounded-xl" data-testid="profile-header-card">
          <div className="flex items-center gap-3">
            <VendorAvatar
              vendor={vendorProfile}
              size={40}
              className="border border-gray-200"
              testId="profile-header-avatar"
            />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm text-gray-900 truncate">{vendorProfile.business_name || 'Your Business'}</p>
              <p className="text-[11px] text-gray-400 truncate">{vendorProfile.city ? `${vendorProfile.city}, ${vendorProfile.state}` : 'Add your location'}</p>
            </div>
          </div>
          <div className="mt-2.5 pt-2.5 border-t border-gray-100 flex items-center justify-between">
            <p className="text-[11px] text-gray-400">See how customers view your profile</p>
            <button
              type="button"
              onClick={() => navigate(`/vendors/${vendorProfile.id}`)}
              className="text-xs text-emerald-600 font-medium hover:text-emerald-700 flex items-center gap-1 transition-colors"
              data-testid="preview-profile-btn"
            >
              <Eye className="h-3.5 w-3.5" /> Preview Profile
            </button>
          </div>
        </div>
      )}

      {/* Storefront-quality strip (not the Go Live gate — see lower "Ready
          to publish?" checklist for the canonical publish-readiness state).
          Tracks visibility-boosting fields (description, portfolio photos,
          pricing). Suppressed while required publish fields are still
          missing so users only see ONE outstanding checklist at a time. */}
      {vendorProfile && (() => {
        const todos = [
          { label: 'Add description', done: vendorProfile.bio?.trim().length > 0 },
          { label: 'Upload photos', done: vendorProfile.portfolio_photos?.filter(p => p?.trim()).length >= 1 },
          { label: 'Set pricing', done: vendorProfile.starting_price?.length > 0 }
        ];
        const pending = todos.filter(t => !t.done);
        if (pending.length === 0) return null;
        // Hide while the canonical publish checklist still has unchecked
        // items (the lower "Ready to publish?" card is the source of truth
        // for Go Live; this strip is purely a boost-your-storefront nudge).
        if (publishReadiness && publishReadiness.missing && publishReadiness.missing.length > 0) return null;
        return (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl" data-testid="storefront-quality-strip">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <p className="text-xs font-semibold text-amber-800">Boost your storefront ({todos.filter(t => t.done).length}/{todos.length})</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {todos.map((t, i) => (
                <span key={i} className={`text-[11px] px-2.5 py-1 rounded-full font-medium ${t.done ? 'bg-green-100 text-green-700' : 'bg-white text-gray-600 border border-gray-200'}`}>
                  {t.done ? <span>&#10003; </span> : null}{t.label}
                </span>
              ))}
            </div>
            <p className="text-[10px] text-amber-700/80 mt-2">These boost visibility — not required to go live.</p>
          </div>
        );
      })()}

      {/* Section Header */}
      <div className="mb-4">
        <h2 className="text-base font-semibold text-gray-900">Build Your Public Profile</h2>
        <p className="text-[11px] text-gray-400 mt-0.5">This information is visible to customers browsing your profile</p>
      </div>

      {/* Main Form */}
      <form onSubmit={handleProfileSubmit} data-testid="profile-card">
        <Accordion type="single" collapsible value={activeSection} onValueChange={(val) => setActiveSection(val || '')} className="space-y-2">
          {/* ═══ BASIC INFO ═══ */}
          <AccordionItem value="basic-info" className="border border-gray-200 rounded-xl overflow-hidden bg-white px-4" data-testid="section-basic-info">
            <AccordionTrigger className="hover:no-underline py-3.5">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center"><User className="w-4 h-4 text-emerald-600" /></div>
                <div className="text-left">
                  <p className="text-sm font-semibold text-gray-900">Basic Info</p>
                  <p className="text-[11px] text-gray-400">Name, location, bio</p>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="space-y-4 pb-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="business_name" className="text-xs">Business Name *</Label>
                  <Input id="business_name" name="business_name" value={profileData.business_name} onChange={handleChange} required placeholder="e.g., Jessica's Balloon Art" data-testid="input-business-name" />
                </div>
                <div>
                  <Label htmlFor="contact_person" className="text-xs">Contact Person *</Label>
                  <Input id="contact_person" name="contact_person" value={profileData.contact_person} onChange={handleChange} required data-testid="input-contact-person" />
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="col-span-2 sm:col-span-4">
                  <button
                    type="button"
                    onClick={handleDetectBusinessLocation}
                    disabled={detectingBizLoc}
                    className="inline-flex items-center gap-1.5 text-xs text-emerald-600 hover:text-emerald-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="detect-business-location-btn"
                  >
                    <Locate className={`h-3 w-3 ${detectingBizLoc ? 'animate-spin' : ''}`} />
                    {detectingBizLoc ? 'Detecting...' : 'Use my business location'}
                  </button>
                </div>
                <div>
                  <Label htmlFor="city" className="text-xs">City *</Label>
                  <Input id="city" name="city" value={profileData.city} onChange={handleChange} data-testid="input-city" />
                </div>
                <div>
                  <Label htmlFor="state" className="text-xs">State *</Label>
                  {/* Canonical internal representation: 2-letter abbreviations
                      (MS, AL, FL). `STATE_ABBR` maps full-name → abbrev. We
                      display the full name as the label but submit the
                      abbrev. Transition compatibility: if profileData.state
                      arrives as a full name (legacy data), we coerce it to
                      the matching abbrev so the select doesn't fall back to
                      the first option ("Alabama") via browser default. */}
                  {(() => {
                    const raw = (profileData.state || '').trim();
                    const isAbbrev = raw.length === 2;
                    const selected = isAbbrev
                      ? raw.toUpperCase()
                      : (STATE_ABBR[raw] || ''); // empty → renders disabled placeholder
                    return (
                      <select
                        id="state"
                        name="state"
                        value={selected}
                        onChange={(e) => setProfileData({ ...profileData, state: e.target.value })}
                        required
                        data-testid="select-state"
                        className="flex w-full items-center rounded-md border border-input bg-transparent px-3 shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring h-9 text-sm"
                      >
                        <option value="" disabled>State</option>
                        {states.map(s => (
                          <option key={s} value={STATE_ABBR[s] || s}>{s}</option>
                        ))}
                      </select>
                    );
                  })()}
                </div>
                <div>
                  <Label htmlFor="zip_code" className="text-xs">Zip *</Label>
                  <Input id="zip_code" name="zip_code" value={profileData.zip_code || ''} onChange={handleChange} placeholder="70123" maxLength={5} required data-testid="input-zip" />
                </div>
                <div>
                  <Label htmlFor="service_radius" className="text-xs">Service Radius (mi) *</Label>
                  <Input id="service_radius" name="service_radius" type="number" value={profileData.service_radius} onChange={handleChange} required data-testid="input-radius" />
                  <p className="text-[11px] text-gray-400 mt-0.5">How far you're willing to travel for events</p>
                </div>
              </div>
              <div>
                <Label htmlFor="short_tagline" className="text-xs">Header Tagline</Label>
                <Input id="short_tagline" name="short_tagline" value={profileData.short_tagline || ''} onChange={handleChange} maxLength={80} placeholder="Making every celebration unforgettable" data-testid="input-short-tagline" />
                <p className="text-[11px] text-gray-400 mt-0.5">Shown at the top of your profile &middot; {(profileData.short_tagline || '').length}/80</p>
              </div>
              <div>
                <Label htmlFor="bio" className="text-xs">About Your Business</Label>
                <Textarea id="bio" name="bio" value={profileData.bio || ''} onChange={handleChange} rows={3} placeholder="Tell customers about your services, experience, and what makes you unique..." data-testid="input-bio" />
                <p className="text-[11px] text-gray-400 mt-0.5">Shown to customers on your profile page</p>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* ═══ SERVICES & OFFERINGS ═══ */}
          <AccordionItem value="services" className="border border-gray-200 rounded-xl overflow-hidden bg-white px-4" data-testid="section-services">
            <AccordionTrigger className="hover:no-underline py-3.5">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center"><Briefcase className="w-4 h-4 text-blue-600" /></div>
                <div className="text-left">
                  <p className="text-sm font-semibold text-gray-900">Services & Offerings</p>
                  <p className="text-[11px] text-gray-400">{totalOfferings > 0 ? `${totalOfferings} offerings — customers request quotes for these` : 'What customers can request quotes for'}</p>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="space-y-4 pb-5">
              {/* B-EXP-6.29 (presentation only) — three compact views inside
                  the one Services & Offerings section to kill the long
                  mobile scroll. Fields, payloads and validation unchanged. */}
              <Tabs defaultValue="what" className="w-full">
                <TabsList className="w-full h-auto grid grid-cols-3 gap-1 bg-gray-100 rounded-lg p-1">
                  <TabsTrigger value="what" className="text-[11px] px-1.5 py-1.5 rounded-md data-[state=active]:bg-white" data-testid="services-tab-what">
                    What You Do
                    {(profileData.categories || []).length > 0
                      ? <Check className="w-3 h-3 ml-1 text-emerald-600" />
                      : <span className="ml-1 text-amber-500">*</span>}
                  </TabsTrigger>
                  <TabsTrigger value="offerings" className="text-[11px] px-1.5 py-1.5 rounded-md data-[state=active]:bg-white" data-testid="services-tab-offerings">
                    Your Services
                    {totalOfferings > 0 && (
                      <span className="ml-1 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-emerald-100 text-emerald-700 text-[9px] font-bold leading-none" data-testid="services-count-badge">
                        {totalOfferings}
                      </span>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="pricing" className="text-[11px] px-1.5 py-1.5 rounded-md data-[state=active]:bg-white" data-testid="services-tab-pricing">
                    Pricing &amp; Details
                    {(Array.isArray(profileData.starting_price)
                      ? profileData.starting_price.length > 0
                      : Boolean(profileData.starting_price)) && <Check className="w-3 h-3 ml-1 text-emerald-600 shrink-0" data-testid="pricing-complete-check" />}
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="what" className="space-y-4 mt-3" data-testid="services-view-what">
              {/* Phase 5B — Business Type (vendor identity layer).
                  Ambient single-select that guides the category picker below
                  without restricting it. Legacy vendors without a type set
                  see a small "set your business type" nudge that pre-fills
                  the backend-suggested value. */}
              {(() => {
                const selectedType = profileData.vendor_type || null;
                const suggestedType = onboarding?.status?.vendor_type_suggested || inferVendorTypeFromCategories(profileData.categories || []);
                return (
                  <div data-testid="business-type-picker">
                    <div className="flex items-center justify-between mb-1.5">
                      <Label className="text-xs">Business Type</Label>
                      {!selectedType && suggestedType && VENDOR_TYPE_META[suggestedType] && (
                        <button
                          type="button"
                          onClick={() => setProfileData({ ...profileData, vendor_type: suggestedType })}
                          className="text-[11px] text-emerald-600 hover:text-emerald-700 font-medium"
                          data-testid="business-type-suggestion"
                        >
                          Use suggested: {VENDOR_TYPE_META[suggestedType].short} &rarr;
                        </button>
                      )}
                    </div>
                    <p className="text-[11px] text-gray-400 mb-1.5">Sets the tone for your profile and helps customers find you faster.</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                      {VENDOR_TYPE_KEYS.map(key => {
                        const meta = VENDOR_TYPE_META[key];
                        const isSelected = selectedType === key;
                        const Icon = meta.icon;
                        return (
                          <button
                            key={key}
                            type="button"
                            onClick={() => setProfileData({ ...profileData, vendor_type: isSelected ? null : key })}
                            data-testid={`business-type-${key}`}
                            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-left text-xs transition-colors ${
                              isSelected
                                ? 'border-emerald-500 bg-emerald-50 text-emerald-900'
                                : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                            }`}
                          >
                            <Icon className="h-3 w-3 shrink-0" />
                            <span className="truncate">{meta.short}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {/* Categories — Phase 5B: sorted by vendor_type when set, so
                  the most relevant options surface first. The full list is
                  always accessible below — guidance, not restriction. */}
              <div>
                <Label className="text-xs">Service Categories *</Label>
                {(profileData.categories || []).length > 0 && (
                  <div className="mt-1.5 mb-2 flex flex-wrap gap-1.5">
                    {(profileData.categories || []).map(cat => {
                      const mismatch = profileData.vendor_type && !isCategoryAllowedForType(profileData.vendor_type, cat);
                      return (
                        <Badge
                          key={cat}
                          variant="default"
                          className={`cursor-pointer text-xs ${mismatch ? 'bg-amber-100 text-amber-900 hover:bg-amber-200' : ''}`}
                          title={mismatch ? 'Unusual combination for your business type' : undefined}
                          onClick={() => toggleCategory(cat)}
                          data-testid={mismatch ? `category-chip-mismatch-${cat}` : undefined}
                        >
                          {cat} <X className="ml-1 h-3 w-3" />
                        </Badge>
                      );
                    })}
                  </div>
                )}
                {(profileData.categories || []).length > 1 && (
                  <div className="mb-2">
                    <Label className="text-[11px] text-gray-500">Primary service</Label>
                    <Select value={profileData.primary_service && profileData.categories?.includes(profileData.primary_service) ? profileData.primary_service : undefined} onValueChange={(v) => setProfileData(prev => ({ ...prev, primary_service: v }))}>
                      <SelectTrigger className="h-8 text-xs" data-testid="primary-service-select"><SelectValue placeholder="Select primary" /></SelectTrigger>
                      <SelectContent>{(profileData.categories || []).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                <Input placeholder="Search categories..." value={categorySearch} onChange={(e) => setCategorySearch(e.target.value)} className="mb-1.5 h-8 text-xs" />
                {(() => {
                  const vtype = profileData.vendor_type;
                  const allowedSet = new Set(vtype ? (VENDOR_TYPE_TO_ALLOWED_CATEGORIES[vtype] || []) : []);
                  const unselected = (categories || []).filter(c => c.toLowerCase().includes(categorySearch.toLowerCase()) && !(profileData.categories || []).includes(c));
                  const primary = vtype ? unselected.filter(c => allowedSet.has(c)) : [];
                  const rest = vtype ? unselected.filter(c => !allowedSet.has(c)) : unselected;
                  return (
                    <div className="border rounded-lg p-2 max-h-52 overflow-y-auto bg-gray-50 space-y-2">
                      {primary.length > 0 && (
                        <div data-testid="categories-primary-bucket">
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1">Recommended for {VENDOR_TYPE_META[vtype]?.short}</p>
                          <div className="grid grid-cols-2 gap-1.5">
                            {primary.map(cat => (
                              <Badge key={cat} variant="outline" className="cursor-pointer justify-start py-1.5 text-xs hover:bg-emerald-50 border-emerald-200 bg-white whitespace-normal min-w-0 break-words leading-tight text-left" onClick={() => toggleCategory(cat)} data-testid={`category-${cat}`}>
                                + {cat}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      {rest.length > 0 && (
                        <div data-testid="categories-other-bucket">
                          {vtype && primary.length > 0 && (
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1 mt-2">Other categories</p>
                          )}
                          <div className="grid grid-cols-2 gap-1.5">
                            {rest.map(cat => (
                              <Badge key={cat} variant="outline" className="cursor-pointer justify-start py-1.5 text-xs hover:bg-emerald-50 whitespace-normal min-w-0 break-words leading-tight text-left" onClick={() => toggleCategory(cat)} data-testid={`category-${cat}`}>
                                + {cat}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

                </TabsContent>

                <TabsContent value="offerings" className="mt-3" data-testid="services-view-offerings">
              {/* Services Summary + Manage Button */}
              <div className="p-3.5 bg-gray-50 border border-gray-100 rounded-xl" data-testid="services-summary">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Your Offerings</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {totalOfferings === 0
                        ? 'No offerings added yet'
                        : `${pkgCount > 0 ? `${pkgCount} package${pkgCount > 1 ? 's' : ''}` : ''}${pkgCount > 0 && svcCount > 0 ? ' and ' : ''}${svcCount > 0 ? `${svcCount} service${svcCount > 1 ? 's' : ''}` : ''}`
                      }
                    </p>
                  </div>
                  <Button type="button" size="sm" className="h-9 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-xs font-semibold" onClick={() => setServicesEditorOpen(true)} data-testid="manage-services-btn">
                    {totalOfferings === 0 ? 'Add Services' : 'Manage'} <ChevronRight className="w-3.5 h-3.5 ml-1" />
                  </Button>
                </div>
                {/* Quick list */}
                {totalOfferings > 0 && (
                  <div className="mt-2.5 space-y-1">
                    {(profileData.services || []).slice(0, 3).map((s, i) => (
                      <div key={i} className="flex items-center justify-between text-xs px-2.5 py-1.5 bg-white rounded-lg">
                        <div className="flex items-center gap-2 min-w-0">
                          {s.is_most_popular && <Star className="w-3 h-3 text-amber-500 fill-amber-500 shrink-0" />}
                          <span className="truncate text-gray-700">{s.name || '(untitled)'}</span>
                          {s.type === 'package' && <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-600 font-medium shrink-0">PKG</span>}
                        </div>
                        {s.starting_price && <span className="text-emerald-600 font-semibold shrink-0 ml-2">{s.starting_price}</span>}
                      </div>
                    ))}
                    {totalOfferings > 3 && <p className="text-[11px] text-gray-400 text-center">+{totalOfferings - 3} more</p>}
                  </div>
                )}
              </div>
                </TabsContent>

                <TabsContent value="pricing" className="space-y-4 mt-3" data-testid="services-view-pricing">
              {/* Price Range */}
              <div>
                <Label className="text-xs">Starting Price Range</Label>
                <p className="text-[11px] text-gray-400 mb-1">Shown to customers as your starting price</p>
                <div className="grid grid-cols-3 gap-1.5 mt-1.5">
                  {(priceRanges || []).map(price => (
                    <Badge key={price} variant={(profileData.starting_price || []).includes(price) ? 'default' : 'outline'} className="cursor-pointer justify-center py-1.5 text-xs" onClick={() => { setProfileData({ ...profileData, starting_price: (profileData.starting_price || []).includes(price) ? [] : [price] }); }} data-testid={`price-${price}`}>
                      {price}
                    </Badge>
                  ))}
                </div>

                {/* Phase 5 — pricing sanity hint. Min dollar price across
                    ALL legitimate standalone offerings (packages + non-addon
                    à-la-carte). Matches the customer display rule exactly
                    (utils/pricing.js). Non-blocking. */}
                {(() => {
                  const bucket = (profileData.starting_price || [])[0];
                  const ADDON_RE = [
                    /\bextra hour\b/i,
                    /\badditional hour\b/i,
                    /\badditional\s+\d+\s+hour/i,
                    /\badd[-\s]?on\b/i,
                    /\btravel fee\b/i,
                    /\btravel charge\b/i,
                    /\brush fee\b/i,
                    /\bsetup fee\b/i,
                  ];
                  const isAddOn = (s) => {
                    if (s?.is_addon === true) return true;
                    const n = String(s?.name || '');
                    return ADDON_RE.some((re) => re.test(n));
                  };
                  const rawPrices = (profileData.services || [])
                    .filter((s) => s && s.available !== false)
                    .filter((s) => {
                      // Include packages, non-addon services, and untyped
                      // items (treat as standalone unless add-on-named).
                      if (s.type === 'package') return true;
                      if (s.type === 'service') return !isAddOn(s);
                      if (!s.type) return !isAddOn(s);
                      return false;
                    })
                    .map((s) => {
                      if (!s?.starting_price) return null;
                      const cleaned = String(s.starting_price).replace(/[^0-9.]/g, '');
                      const n = parseFloat(cleaned);
                      return Number.isFinite(n) && n > 0 ? n : null;
                    })
                    .filter(Boolean);
                  if (!bucket || rawPrices.length === 0) return null;
                  const derivedMin = Math.min(...rawPrices);
                  const BUCKET_RANGES = {
                    'Under $250':      [0, 250],
                    '$250 - $500':     [250, 500],
                    '$500 - $1,000':   [500, 1000],
                    '$1,000 - $2,500': [1000, 2500],
                    '$2,500 - $5,000': [2500, 5000],
                    '$5,000+':         [5000, Infinity],
                  };
                  const range = BUCKET_RANGES[bucket];
                  if (!range) return null;
                  const inRange = derivedMin >= range[0] && derivedMin <= range[1];
                  if (inRange) return null;
                  return (
                    <p className="text-[11px] text-amber-700 mt-2" data-testid="pricing-mismatch-hint">
                      Heads-up: your lowest offering is ${derivedMin.toLocaleString()}, which doesn&apos;t quite match the &ldquo;{bucket}&rdquo; range. Customers will see &ldquo;Starting at ${derivedMin.toLocaleString()}&rdquo; on your profile.
                    </p>
                  );
                })()}

                {/* Phase 1: read-only platform fee status (standardized) */}
                {feeStatus && (
                  <div className="mt-3" data-testid="profile-fee-status-line">
                    <FeeStatusPill feeStatus={feeStatus} variant="pill" fallback="line" />
                  </div>
                )}
              </div>

              {/* Specialties (optional) */}
              <div>
                <Label className="text-xs">Specialties <span className="text-gray-400 font-normal">(optional)</span></Label>
                <p className="text-[11px] text-gray-400 mb-1">Short tags shown in planner previews — e.g. “Weddings”, “Kids' parties”</p>
                <div className="flex flex-wrap gap-1.5 mb-1.5 mt-1">
                  {(profileData.specialties || []).map((spec, idx) => (
                    <Badge key={idx} variant="default" className="cursor-pointer text-xs" onClick={() => { const u = [...(profileData.specialties || [])]; u.splice(idx, 1); setProfileData({ ...profileData, specialties: u }); }} data-testid={`specialty-chip-${idx}`}>
                      {spec} <X className="ml-1 h-3 w-3" />
                    </Badge>
                  ))}
                </div>
                <Input placeholder="Press Enter to add" className="h-8 text-xs" data-testid="input-specialty" onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); const v = e.target.value.trim(); if (v && !(profileData.specialties || []).includes(v)) { setProfileData({ ...profileData, specialties: [...(profileData.specialties || []), v] }); e.target.value = ''; } } }} />
              </div>
                </TabsContent>
              </Tabs>
            </AccordionContent>
          </AccordionItem>

          {/* ═══ PHOTOS & GALLERY ═══ */}
          <AccordionItem value="photos" className="border border-gray-200 rounded-xl overflow-hidden bg-white px-4" data-testid="section-photos">
            <AccordionTrigger className="hover:no-underline py-3.5">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center"><ImageIcon className="w-4 h-4 text-purple-600" /></div>
                <div className="text-left">
                  <p className="text-sm font-semibold text-gray-900">Photos & Gallery</p>
                  <p className="text-[11px] text-gray-400">{profileData.profile_photo ? '1 profile' : 'No profile'}{photoCount > 0 ? `, ${photoCount} portfolio` : ''} &middot; Customers see these when deciding to book</p>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="space-y-4 pb-5">
              <div>
                <Label className="text-xs">Profile Photo *</Label>
                <Input type="file" accept=".jpg,.jpeg,.png" onChange={handleProfileImageUpload} disabled={uploadingImage} required={!profileData.profile_photo} data-testid="input-profile-photo" className="mt-1" />
                {profileData.profile_photo ? (
                  <>
                    <div className="flex items-center gap-3 mt-2">
                      <img src={getImageUrl(profileData.profile_photo)} alt="Profile" className="h-16 w-16 object-cover rounded-lg border" onError={(e) => { e.target.style.display = 'none'; }} />
                      <Button type="button" variant="destructive" size="sm" onClick={removeProfilePhoto} data-testid="remove-profile-photo">Remove</Button>
                    </div>
                    {hasOwnerPhoto(vendorProfile) && (
                      <div className="mt-2 flex items-center gap-2 text-[11px] text-gray-500" data-testid="owner-photo-source-note">
                        <img src={vendorProfile.owner_photo} alt="" className="h-5 w-5 rounded-full object-cover border border-gray-200 opacity-80" onError={(e) => { e.target.style.display = 'none'; }} />
                        <span className="flex-1">
                          <span className="font-medium text-gray-700">Using your business photo</span>
                          <span className="text-gray-300 mx-1.5">&middot;</span>
                          <span>Personal photo available</span>
                        </span>
                        <button
                          type="button"
                          onClick={removeProfilePhoto}
                          className="text-emerald-700 font-medium hover:text-emerald-800 underline-offset-2 hover:underline"
                          data-testid="use-personal-photo-btn"
                        >
                          Use personal photo
                        </button>
                      </div>
                    )}
                  </>
                ) : isUsingOwnerPhotoFallback(vendorProfile) && (
                  <div className="flex items-center gap-3 mt-2" data-testid="owner-photo-fallback-preview">
                    <img src={vendorProfile.owner_photo} alt="Your personal photo" className="h-16 w-16 object-cover rounded-lg border border-dashed border-emerald-300 opacity-90" onError={(e) => { e.target.style.display = 'none'; }} />
                    <div className="text-[11px] text-gray-600 leading-snug">
                      <p className="font-medium text-emerald-700">Using your personal photo</p>
                      <p className="text-gray-400">Upload a business logo to replace it.</p>
                    </div>
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs">Portfolio Photos</Label>
                <p className="text-[11px] text-gray-400 mb-2">Showcase your work &middot; tap <Crown className="inline w-3 h-3 text-emerald-600 -mt-0.5" /> on any photo to feature it at the top of your profile</p>
                <div className="space-y-2">
                  {(profileData.portfolio_photos || []).map((photo, idx) => {
                    const isHero = !!(photo && profileData.cover_image && photo === profileData.cover_image);
                    return (
                    <div key={idx} className="flex items-start gap-2">
                      <div className="flex-1">
                        <Input type="file" accept=".jpg,.jpeg,.png" onChange={(e) => handlePortfolioImageUpload(e, idx)} disabled={uploadingImage} data-testid={`input-portfolio-${idx}`} />
                        {photo && (
                          <div className="flex items-center gap-2 mt-1.5">
                            <div className="relative">
                              <img
                                src={getImageUrl(photo)}
                                alt={`Portfolio ${idx + 1}`}
                                className={`h-14 w-14 object-cover rounded border transition-all ${isHero ? 'ring-2 ring-emerald-500 ring-offset-1' : ''}`}
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                              {isHero && (
                                <span
                                  className="absolute -top-1.5 -left-1.5 bg-emerald-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-sm flex items-center gap-0.5 pointer-events-none"
                                  data-testid={`hero-badge-${idx}`}
                                >
                                  <Crown className="w-2.5 h-2.5" /> Hero
                                </span>
                              )}
                            </div>
                            <Button
                              type="button"
                              variant={isHero ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => handleSetHero && handleSetHero(photo)}
                              data-testid={`set-hero-${idx}`}
                              className={isHero ? 'bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8' : 'text-xs h-8 border-emerald-200 text-emerald-700 hover:bg-emerald-50'}
                              title={isHero ? 'Click to clear hero selection' : 'Feature this photo at the top of your profile'}
                            >
                              <Crown className="w-3 h-3 mr-1" />
                              {isHero ? 'Hero · Clear' : 'Set as Hero'}
                            </Button>
                            <Button type="button" variant="destructive" size="sm" onClick={() => removePortfolioPhoto(idx)} data-testid={`remove-portfolio-${idx}`}>Remove</Button>
                          </div>
                        )}
                      </div>
                      {idx >= 3 && <button type="button" onClick={() => removePortfolioField(idx)} className="text-gray-400 hover:text-red-500 mt-2"><X className="h-4 w-4" /></button>}
                    </div>
                    );
                  })}
                </div>
                <Button type="button" variant="outline" size="sm" onClick={addPortfolioField} className="mt-2 text-xs" disabled={uploadingImage} data-testid="add-portfolio-btn">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add Photo
                </Button>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* ═══ PREFERENCES & SOCIAL ═══ */}
          <AccordionItem value="preferences" className="border border-gray-200 rounded-xl overflow-hidden bg-white px-4" data-testid="section-preferences">
            <AccordionTrigger className="hover:no-underline py-3.5">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center"><Settings className="w-4 h-4 text-gray-600" /></div>
                <div className="text-left">
                  <p className="text-sm font-semibold text-gray-900">Contact & Social</p>
                  <p className="text-[11px] text-gray-400">Email, phone, links, sharing</p>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="space-y-4 pb-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label className="text-xs">Email *</Label><Input id="email" name="email" type="email" value={profileData.email} onChange={handleChange} required data-testid="input-email" /></div>
                <div><Label className="text-xs">Phone *</Label><Input id="phone" name="phone" value={profileData.phone} onChange={handleChange} required data-testid="input-phone" /></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label className="text-xs">Website</Label><Input name="website" value={profileData.website} onChange={handleChange} placeholder="https://..." data-testid="input-website" /></div>
                <div><Label className="text-xs">Instagram</Label><Input name="instagram" value={profileData.instagram} onChange={handleChange} placeholder="@username" data-testid="input-instagram" /></div>
                <div><Label className="text-xs">TikTok</Label><Input name="tiktok" value={profileData.tiktok} onChange={handleChange} placeholder="@username" data-testid="input-tiktok" /></div>
                <div><Label className="text-xs">Facebook</Label><Input name="facebook" value={profileData.facebook} onChange={handleChange} placeholder="https://facebook.com/..." data-testid="input-facebook" /></div>
              </div>

              {/* Payments note */}
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-start gap-2.5">
                <DollarSign className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                <p className="text-xs text-gray-600"><span className="font-medium text-gray-900">Payments handled securely.</span> All payments processed through Stripe.</p>
              </div>

              {/* Share Profile */}
              {vendorProfile && (
                <div className="pt-3 border-t border-gray-100">
                  <p className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1.5"><Share2 className="w-3.5 h-3.5" /> Share Profile</p>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" size="sm" className="flex-1 text-xs"
                      onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/vendors/${vendorProfile?.id}`); setProfileLinkCopied(true); toast.success('Link copied!'); setTimeout(() => setProfileLinkCopied(false), 2000); }}>
                      {profileLinkCopied ? <Check className="h-3.5 w-3.5 mr-1" /> : <Copy className="h-3.5 w-3.5 mr-1" />} {profileLinkCopied ? 'Copied' : 'Copy Link'}
                    </Button>
                    <Button type="button" variant="outline" size="sm" className="text-xs" onClick={() => shareOnFacebook(`${window.location.origin}/vendors/${vendorProfile?.id}`)}>Facebook</Button>
                    <Button type="button" variant="outline" size="sm" className="text-xs" onClick={async () => { const u = encodeURIComponent(`${window.location.origin}/vendors/${vendorProfile?.id}`); await openShareUrl(`https://wa.me/?text=${encodeURIComponent(`Check out my services! ${decodeURIComponent(u)}`)}`); }}>WhatsApp</Button>
                  </div>
                </div>
              )}

              {/* Referral */}
              {referralLink && (
                <div className="pt-3 border-t border-gray-100">
                  <p className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1.5"><Gift className="w-3.5 h-3.5 text-emerald-600" /> Referral Link</p>
                  <div className="flex items-center gap-2 p-2.5 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-[11px] font-mono text-gray-600 truncate min-w-0 flex-1">{referralLink}</p>
                    <Button type="button" variant="outline" size="sm" className="text-xs shrink-0 h-7"
                      onClick={() => { navigator.clipboard.writeText(referralLink); setReferralLinkCopied(true); toast.success(TOAST.REFERRAL_LINK_COPIED); setTimeout(() => setReferralLinkCopied(false), 2000); }}>
                      {referralLinkCopied ? <><Check className="w-3 h-3 mr-1" /> Copied</> : <><Copy className="w-3 h-3 mr-1" /> Copy</>}
                    </Button>
                  </div>
                </div>
              )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        {/* Publish readiness checklist (draft state only) */}
        {!isLive && publishReadiness && (
          <div className="mt-4 p-4 bg-gray-50 rounded-xl border border-gray-100" data-testid="publish-checklist">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2.5">Ready to publish?</p>
            <div className="space-y-1.5">
              {(() => {
                // Phase 4: prefer backend-canonical phase missing list when available.
                // Map `missing` labels to the same section-anchor scheme used by the
                // legacy checkable list so "jump to fix" behavior is preserved.
                if (useCanonical) {
                  const REQUIRED_CHECK_ITEMS = [
                    { label: 'Business Name',          section: 'identity'   },
                    { label: 'Contact Person',         section: 'identity'   },
                    { label: 'Service Area (State)',   section: 'identity'   },
                    { label: 'Service Area (ZIP)',     section: 'identity'   },
                    { label: 'Profile Photo',          section: 'identity'   },
                    { label: 'Email',                  section: 'identity'   },
                    { label: 'Phone',                  section: 'identity'   },
                    { label: 'At least 1 Category',    section: 'categories' },
                  ];
                  const missingSet = new Set(canonicalMissing);
                  return REQUIRED_CHECK_ITEMS.map(item => {
                    const ok = !missingSet.has(item.label);
                    return (
                      <div key={item.label} className="flex items-center gap-2 text-sm">
                        {ok ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                        ) : (
                          <button
                            type="button"
                            onClick={() => { setActiveSection(item.section); setTimeout(() => { const el = document.querySelector(`[data-testid="section-${item.section}"]`); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 150); }}
                            className="w-4 h-4 rounded-full border-2 border-gray-300 shrink-0 hover:border-emerald-400 transition-colors"
                          />
                        )}
                        <span className={ok ? 'text-gray-600' : 'text-gray-400'}>{item.label}</span>
                      </div>
                    );
                  });
                }
                // Legacy fallback (kept so impersonation / context-less paths still render correctly).
                return publishReadiness.checks.map(check => (
                  <div key={check.label} className="flex items-center gap-2 text-sm">
                    {check.ok ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                    ) : (
                      <button
                        type="button"
                        onClick={() => { setActiveSection(check.section); setTimeout(() => { const el = document.querySelector(`[data-testid="section-${check.section}"]`); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 150); }}
                        className="w-4 h-4 rounded-full border-2 border-gray-300 shrink-0 hover:border-emerald-400 transition-colors"
                      />
                    )}
                    <span className={check.ok ? 'text-gray-600' : 'text-gray-400'}>{check.label}</span>
                  </div>
                ));
              })()}
            </div>
          </div>
        )}

        {/* Save / Publish buttons */}
        <div className="mt-4 space-y-2">
          {isLive ? (
            /* Live: single save */
            <Button type="submit" disabled={loading} className="w-full h-11 rounded-xl bg-emerald-700 hover:bg-emerald-800" data-testid="save-profile-btn">
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          ) : publishReadiness?.isReady ? (
            /* Draft + publish-ready: primary Go Live, secondary Save Draft */
            <>
              <Button type="button" onClick={handleGoLive} disabled={loading} className="w-full h-11 rounded-xl bg-emerald-700 hover:bg-emerald-800" data-testid="go-live-btn">
                {loading ? 'Publishing...' : 'Go Live'}
              </Button>
              <Button type="submit" variant="ghost" disabled={loading} className="w-full h-9 text-sm text-gray-500 hover:text-gray-700" data-testid="save-draft-btn">
                {loading ? 'Saving...' : 'Save Draft'}
              </Button>
            </>
          ) : (
            /* Draft + not ready: primary Save Draft, disabled Go Live */
            <>
              <Button type="submit" disabled={loading} className="w-full h-11 rounded-xl" data-testid="save-draft-btn">
                {loading ? 'Saving...' : 'Save Draft'}
              </Button>
              <Button type="button" disabled className="w-full h-9 rounded-xl text-sm opacity-50" variant="outline" data-testid="go-live-btn-disabled">
                Go Live
              </Button>
            </>
          )}
        </div>
      </form>

      {/* Services Editor Modal */}
      <ServicesEditorModal
        open={servicesEditorOpen}
        onClose={() => setServicesEditorOpen(false)}
        profileData={profileData}
        setProfileData={setProfileData}
      />
    </>
  );
};

export default VendorProfileForm;
